import { toZonedTime } from "date-fns-tz"
import { localDateOnlyRangeToUtcRange } from "@ibada/shared-types"

import { PeriodPreset, resolvePeriodRange } from "@/shared/lib/dates/period-range"

/**
 * The presets the transfer calculator offers, in the order a treasurer thinks about remittance: the recent
 * past first, because that is what is being remitted, with Custom last as the escape hatch.
 *
 * Deliberately a subset of {@link PeriodPreset}. Rolling windows ("last six months") and fiscal presets are
 * reporting vocabulary, not remittance vocabulary — nobody remits "the last twelve months" on a schedule.
 */
export const DERIVATION_PRESETS: readonly PeriodPreset[] = [
  PeriodPreset.Today,
  PeriodPreset.Yesterday,
  PeriodPreset.WeekToDate,
  PeriodPreset.LastWeek,
  PeriodPreset.MonthToDate,
  PeriodPreset.LastMonth,
  PeriodPreset.QuarterToDate,
  PeriodPreset.LastQuarter,
  PeriodPreset.YearToDate,
  PeriodPreset.Custom,
]

export type DerivationRange = { from: string; to: string }

/**
 * Resolve the chosen period into the two instants the server will calculate over.
 *
 * Resolved on the CLIENT because a preset means different things in different timezones and only the client
 * knows the workspace's — "last week" for a church in Kigali is not the same span of instants as the server's
 * idea of it. The preset label rides along separately, purely so the duplicate warning can name it.
 *
 * Returns null when the range cannot be formed (Custom with a missing or backwards date), which is the
 * caller's signal that there is nothing to calculate yet.
 */
export function resolveDerivationRange(input: {
  preset: PeriodPreset
  timeZone: string
  customFrom?: string
  customTo?: string
}): DerivationRange | null {
  if (input.preset === PeriodPreset.Custom) {
    if (!input.customFrom || !input.customTo) return null
    // Whole days, inclusive at both ends: a remittance covers days, not moments.
    try {
      const range = localDateOnlyRangeToUtcRange({
        from: input.customFrom,
        to: input.customTo,
        timeZone: input.timeZone,
      })
      if (new Date(range.from).getTime() > new Date(range.to).getTime()) return null
      return range
    } catch {
      return null
    }
  }

  const zonedNow = toZonedTime(new Date(), input.timeZone)
  const range = resolvePeriodRange(input.preset, { now: zonedNow })
  if (!range) return null
  return { from: range.start.toISOString(), to: range.end.toISOString() }
}

/**
 * Percentage -> money, for the manual shortcut beside the Amount field.
 *
 * Works in integer MINOR UNITS rather than floats: 10% of 33.33 through `Number` arithmetic lands on
 * 3.3330000000000004, and a money field is not the place to discover binary fractions.
 *
 * Returns null when there is no basis (no fund chosen, or an unusable percentage) — the caller leaves the
 * amount alone rather than writing a zero the user did not ask for.
 */
export function amountFromPercentage(input: {
  /** The fund's AVAILABLE balance as a canonical decimal string — what it may still commit. */
  available: string | null | undefined
  percentage: string
  /** Decimal places the currency actually has: 2 for USD, 0 for RWF. */
  scale: number
}): string | null {
  // An EMPTY field is not zero percent. `Number("")` is 0, so without this a user clearing the percentage
  // would watch their amount silently reset to 0.00 — a destructive answer to "I have not decided yet".
  if (!input.percentage.trim()) return null
  const percent = Number(input.percentage)
  if (!input.available || !Number.isFinite(percent) || percent < 0) return null

  const minorUnits = toMinorUnits(input.available, input.scale)
  if (minorUnits === null) return null

  const scaled = Math.round((minorUnits * percent) / 100)
  return fromMinorUnits(scaled, input.scale)
}

/**
 * Money -> percentage, the reverse direction of the same shortcut.
 *
 * Rounded to one decimal so an exact half of an odd amount reads as "50%" rather than "49.9999%". Null when
 * there is nothing to be a percentage OF — a zero balance has no meaningful share.
 */
export function percentageFromAmount(input: {
  available: string | null | undefined
  amount: string
  scale: number
}): string | null {
  if (!input.available || !input.amount) return null

  const availableMinor = toMinorUnits(input.available, input.scale)
  const amountMinor = toMinorUnits(input.amount, input.scale)
  if (availableMinor === null || amountMinor === null || availableMinor === 0) return null

  return String(Math.round((amountMinor / availableMinor) * 1000) / 10)
}

/** A decimal money string as an integer count of minor units, or null if it is not a money string. */
function toMinorUnits(value: string, scale: number): number | null {
  const trimmed = value.trim()
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  return Math.round(parsed * 10 ** scale)
}

function fromMinorUnits(minorUnits: number, scale: number): string {
  return (minorUnits / 10 ** scale).toFixed(scale)
}

/**
 * Is the transfer complete enough for an amount to be entered or calculated?
 *
 * Not merely tidiness. Saving the calculation as a reusable rule stores the destination, account and fund
 * ALONGSIDE the criteria — so allowing Calculate before they are chosen would let a rule be saved pointing at
 * nothing, which fails later, on a screen far from the mistake. And a percentage of "the fund" means nothing
 * before a fund exists.
 */
export function canEnterTransferAmount(values: {
  destinationBranchId?: string | null
  sourceAccountId?: string | null
  sourceFundId?: string | null
}): boolean {
  return Boolean(
    values.destinationBranchId?.trim() && values.sourceAccountId?.trim() && values.sourceFundId?.trim(),
  )
}

/**
 * Is the transfer complete enough to send at all — draft included?
 *
 * The backend requires destination, account, fund AND amount even for a DRAFT, so an incomplete form has
 * nothing to submit and nothing to save: pressing either would be a request guaranteed to fail. Disabling
 * says that before the click rather than after it.
 */
export function canSubmitTransfer(values: {
  destinationBranchId?: string | null
  sourceAccountId?: string | null
  sourceFundId?: string | null
  amount?: string | null
}): boolean {
  return canEnterTransferAmount(values) && Boolean(values.amount?.trim())
}

/** The identity of a saved calculation: same source, same period, same share is the same rule. */
export type DerivationCriteria = {
  mode: string
  sourceId: string
  preset: string
  percentage: number | string
}

/**
 * Has this exact rule already been saved?
 *
 * Two entries with the same source, period and share are indistinguishable in the list, and neither can be
 * trusted as the one that runs. The NAME is deliberately not part of the identity — "Weekly tithes" and
 * "Tithes weekly" pointing at the same calculation are still one rule saved twice.
 *
 * Percentage is compared numerically: 50 and "50.0" are the same share, and a string comparison would let the
 * duplicate through.
 */
export function isDuplicateCriteria(
  candidate: DerivationCriteria,
  existing: ReadonlyArray<DerivationCriteria>,
): boolean {
  return existing.some(
    (item) =>
      item.mode === candidate.mode &&
      item.sourceId === candidate.sourceId &&
      item.preset === candidate.preset &&
      Number(item.percentage) === Number(candidate.percentage),
  )
}
